import {
  Button,
  Switch,
  Dialog,
  DialogActions,
  DialogTitle,
  FormControlLabel,
  Stack,
  TextField,
  Card,
  CardContent,
  Typography,
  Box,
  Divider,
  Chip,
  FormControl,
  FormLabel,
  RadioGroup,
  Radio,
} from "@mui/material";
import {
  SportsEsports,
  Star,
  Lock,
  ThumbUp,
  EmojiEvents,
  Settings,
  Create,
  Timeline,
  TextFields,
} from "@mui/icons-material";
import React, { ChangeEvent, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useLeague } from "../../hooks/useLeague/useLeague";
import { CreateLeagueRequest } from "../../types/league";

const handleTextInputChange = (
  event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  minVal: number,
  maxVal: number,
  setValue: (value: number) => void,
  updateError: (value: boolean) => void
) => {
  const num = parseInt(event.target.value);
  if (isNaN(num)) {
    updateError(true);
    return;
  }
  if (num < minVal || num > maxVal) {
    updateError(true);
    return;
  }
  updateError(false);
  event.target.value = String(num);
  setValue(num);
};

const Scoring = ({
  bonus,
  onBonusChange,
  minBonus,
  maxBonus,
  penalty,
  onPenaltyChange,
  minPenalty,
  maxPenalty,
  onErrorChange,
}: {
  bonus: number;
  onBonusChange: (value: number) => void;
  minBonus: number;
  maxBonus: number;
  penalty: number;
  onPenaltyChange: (value: number) => void;
  minPenalty: number;
  maxPenalty: number;
  onErrorChange: (value: boolean) => void;
}) => {
  const [bonusError, setBonusError] = useState<boolean>(false);
  const [penaltyError, setPenaltyError] = useState<boolean>(false);

  const updateBonusError = (error: boolean) => {
    setBonusError(error);
    onErrorChange(error || penaltyError);
  };

  const updatePenaltyError = (error: boolean) => {
    setPenaltyError(error);
    onErrorChange(error || bonusError);
  };

  return (
    <Stack direction="row" spacing={2} sx={{ alignItems: "center" }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Chip
          label={`+${bonus}`}
          color="success"
          variant="outlined"
          icon={<ThumbUp />}
        />
        <TextField
          error={bonusError}
          defaultValue={bonus}
          label="Correct bonus"
          variant="outlined"
          size="small"
          sx={{ width: 120 }}
          onChange={(event) =>
            handleTextInputChange(
              event,
              minBonus.valueOf(),
              maxBonus.valueOf(),
              onBonusChange,
              updateBonusError
            )
          }
          helperText={
            bonusError ? `Choose between ${minBonus} and ${maxBonus}` : ""
          }
        />
      </Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Chip label={`${penalty}`} color="error" variant="outlined" />
        <TextField
          error={penaltyError}
          defaultValue={penalty}
          label="Incorrect penalty"
          variant="outlined"
          size="small"
          sx={{ width: 120 }}
          onChange={(event) =>
            handleTextInputChange(
              event,
              minPenalty.valueOf(),
              maxPenalty.valueOf(),
              onPenaltyChange,
              updatePenaltyError
            )
          }
          helperText={
            penaltyError ? `Choose between ${minPenalty} and ${maxPenalty}` : ""
          }
        />
      </Box>
    </Stack>
  );
};

const CreateLeagueDialog = ({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) => {
  const { createLeague } = useLeague();
  const { user, isAuthenticated, isLoading } = useAuth0();
  const [leagueName, setLeagueName] = useState<string>("Super League");
  const [leagueNameError, setLeagueNameError] = useState<boolean>(false);

  const [numberOfGames, setNumberOfGames] = useState<number>(20);
  const [numberOfGamesError, setNumberOfGamesError] = useState<boolean>(false);
  const [bonus, setBonus] = useState<number>(1);
  const [penalty, setPenalty] = useState<number>(0);
  const [scoringError, setScoringError] = useState<boolean>(false);

  const [gameOfTheWeekEnabled, setGameOfTheWeekEnabled] =
    useState<boolean>(true);
  const [gameOfTheWeekBonus, setGameOfTheWeekBonus] = useState<number>(3);
  const [gameOfTheWeekPenalty, setGameOfTheWeekPenalty] = useState<number>(0);
  const [gameOfTheWeekScoringError, setGameOfTheWeekScoringError] =
    useState<boolean>(false);

  const [locksEnabled, setLocksEnabled] = useState<boolean>(true);
  const [numberOfLocks, setNumberOfLocks] = useState<number>(1);
  const [numberOfLocksError, setNumberOfLocksError] = useState<boolean>(false);
  const [locksAreUnique, setLocksAreUnique] = useState<boolean>(false);
  const [lockBonus, setLockBonus] = useState<number>(20);
  const [lockPenalty, setLockPenalty] = useState<number>(20);
  const [locksScoringError, setLocksScoringError] = useState<boolean>(false);

  const [tiebreakersEnabled, setTiebreakersEnabled] = useState<boolean>(true);
  const [
    tiebreakerDefaultToGameOfTheWeek,
    setTiebreakerDefaultToGameOfTheWeek,
  ] = useState<boolean>(true);

  const [nailsEnabled, setNailsEnabled] = useState<boolean>(true);
  const [trackNailsSeparately, setTrackNailsSeparately] =
    useState<boolean>(true);
  const [nailBonus, setNailBonus] = useState<number>(20);
  const [nailPenalty, setNailPenalty] = useState<number>(20);
  const [nailScoringError, setNailScoringError] = useState<boolean>(false);

  const [pushEnabled, setPushEnabled] = useState<boolean>(false);

  // Season Winner Rules
  const [seasonWinnerOrderBy, setSeasonWinnerOrderBy] = useState<
    "weeklyWins" | "totalPoints"
  >("totalPoints");

  // PickEm-FlavorLines settings
  // Official lines are always on by default; no toggle in UI
  const [customGameLinesEnabled, setCustomGameLinesEnabled] =
    useState<boolean>(false);
  const [hideOfficialWhenCustom, setHideOfficialWhenCustom] =
    useState<boolean>(false);
  const [flavorTextEnabled, setFlavorTextEnabled] = useState<boolean>(false);

  // Prevent multiple submits
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  function handleLeagueNameChange(
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ): void {
    const newName = event.target.value;
    if (!newName) {
      setLeagueNameError(true);
      return;
    }
    setLeagueNameError(false);
    setLeagueName(newName);
  }

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (isSubmitting) return;
    setIsSubmitting(true);

    if (!(!isLoading && isAuthenticated && user && user.email)) {
      setIsSubmitting(false);
      return;
    }

    const req: CreateLeagueRequest = {
      owner: user.email,
      admins: [user.email],
      members: [user.email],
      name: leagueName,
      rules: {
        gameCount: numberOfGames,
        gameScoring: {
          bonus,
          penalty,
        },
        gameOfTheWeekRules: gameOfTheWeekEnabled
          ? {
              bonus: gameOfTheWeekBonus,
              penalty: gameOfTheWeekPenalty,
            }
          : undefined,
        lockRules: locksEnabled
          ? {
              count: numberOfLocks,
              uniqueLock: locksAreUnique,
              scoring: {
                bonus: lockBonus,
                penalty: lockPenalty,
              },
            }
          : undefined,
        tiebreakerRules: tiebreakersEnabled
          ? {
              defaultToGotw: tiebreakerDefaultToGameOfTheWeek,
            }
          : undefined,
        nailRules: nailsEnabled
          ? {
              trackNailsSeparately: trackNailsSeparately,
              scoring: {
                bonus: nailBonus,
                penalty: nailPenalty,
              },
            }
          : undefined,
        seasonWinnerRules: {
          orderBy: seasonWinnerOrderBy,
        },
        pushEnabled: pushEnabled,
        flavorLinesRules: {
          showGameLines: true,
          customGameLinesEnabled: customGameLinesEnabled,
          flavorTextEnabled: flavorTextEnabled,
          hideOfficialLinesWhenCustomEnabled: hideOfficialWhenCustom,
        },
      },
    };

    try {
      const response = await createLeague(req);
      if (response instanceof Error) {
        throw new Error(`Failed to create league: ${response.message}`);
      }
    } catch (error) {
      console.error("Error submitting data: ", error);
    } finally {
      setIsSubmitting(false);
      onClose();
    }
  };

  const validRules = () => {
    return (
      !numberOfGamesError &&
      !scoringError &&
      !gameOfTheWeekScoringError &&
      !numberOfLocksError &&
      !locksScoringError &&
      !nailScoringError
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Create color="primary" />
        Create a Pickem League
      </DialogTitle>
      <Box component="form" noValidate onSubmit={handleSubmit} sx={{ p: 3 }}>
        <Stack spacing={3}>
          {/* League Name */}
          <Card elevation={2}>
            <CardContent>
              <Typography
                variant="h6"
                gutterBottom
                sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}
              >
                <Create color="primary" />
                League Information
              </Typography>
              <TextField
                error={leagueNameError}
                defaultValue={leagueName}
                label="Pickem league name"
                variant="outlined"
                fullWidth
                onChange={handleLeagueNameChange}
                helperText={leagueNameError ? "Name is required" : ""}
              />
            </CardContent>
          </Card>

          {/* Basic Game Rules */}
          <Card elevation={2}>
            <CardContent>
              <Typography
                variant="h6"
                gutterBottom
                sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}
              >
                <SportsEsports color="primary" />
                Basic Game Rules
              </Typography>
              <Stack spacing={2}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <Chip
                    label={`${numberOfGames} Games`}
                    color="primary"
                    variant="outlined"
                  />
                  <TextField
                    error={numberOfGamesError}
                    defaultValue={numberOfGames}
                    label="Number of games"
                    variant="outlined"
                    size="small"
                    sx={{ width: 150 }}
                    onChange={(event) =>
                      handleTextInputChange(
                        event,
                        5,
                        40,
                        setNumberOfGames,
                        setNumberOfGamesError
                      )
                    }
                    helperText={
                      numberOfGamesError ? "Choose between 5 and 40" : ""
                    }
                  />
                </Box>
                <Divider />
                <Scoring
                  bonus={bonus}
                  onBonusChange={setBonus}
                  penalty={penalty}
                  onPenaltyChange={setPenalty}
                  minBonus={1}
                  maxBonus={100}
                  minPenalty={0}
                  maxPenalty={100}
                  onErrorChange={setScoringError}
                />
              </Stack>
            </CardContent>
          </Card>

          {/* Game of the Week */}
          <Card elevation={2}>
            <CardContent>
              <Typography
                variant="h6"
                gutterBottom
                sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}
              >
                <Star color="warning" />
                Game of the Week
              </Typography>
              <Stack spacing={2}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={gameOfTheWeekEnabled}
                      color="warning"
                      size="medium"
                    />
                  }
                  label="Enable Game of the Week"
                  onChange={(event, checked) =>
                    setGameOfTheWeekEnabled(checked)
                  }
                />
                {gameOfTheWeekEnabled && (
                  <Scoring
                    bonus={gameOfTheWeekBonus}
                    onBonusChange={setGameOfTheWeekBonus}
                    penalty={gameOfTheWeekPenalty}
                    onPenaltyChange={setGameOfTheWeekPenalty}
                    minBonus={1}
                    maxBonus={100}
                    minPenalty={0}
                    maxPenalty={100}
                    onErrorChange={setGameOfTheWeekScoringError}
                  />
                )}
              </Stack>
            </CardContent>
          </Card>

          {/* Locks */}
          <Card elevation={2}>
            <CardContent>
              <Typography
                variant="h6"
                gutterBottom
                sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}
              >
                <Lock color="secondary" />
                Locks
              </Typography>
              <Stack spacing={2}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={locksEnabled}
                      color="secondary"
                      size="medium"
                    />
                  }
                  label="Enable Locks"
                  onChange={(event, checked) => setLocksEnabled(checked)}
                />
                {locksEnabled && (
                  <>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <Chip
                        label={locksAreUnique ? "Unique" : "Not Unique"}
                        color={locksAreUnique ? "success" : "default"}
                        variant="outlined"
                      />
                      <FormControlLabel
                        control={
                          <Switch
                            checked={locksAreUnique}
                            color="success"
                            size="medium"
                          />
                        }
                        label="Locks are unique"
                        onChange={(event, checked) =>
                          setLocksAreUnique(checked)
                        }
                      />
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <Chip
                        label={`${numberOfLocks} Locks`}
                        color="secondary"
                        variant="outlined"
                      />
                      <TextField
                        error={numberOfLocksError}
                        defaultValue={numberOfLocks}
                        label="Number of locks per player"
                        variant="outlined"
                        size="small"
                        sx={{ width: 200 }}
                        onChange={(event) =>
                          handleTextInputChange(
                            event,
                            1,
                            3,
                            setNumberOfLocks,
                            setNumberOfLocksError
                          )
                        }
                        helperText={
                          numberOfLocksError ? "Choose between 1 and 3" : ""
                        }
                      />
                    </Box>
                    <Divider />
                    <Scoring
                      bonus={lockBonus}
                      onBonusChange={setLockBonus}
                      penalty={lockPenalty}
                      onPenaltyChange={setLockPenalty}
                      minBonus={1}
                      maxBonus={100}
                      minPenalty={0}
                      maxPenalty={100}
                      onErrorChange={setLocksScoringError}
                    />
                  </>
                )}
              </Stack>
            </CardContent>
          </Card>

          {/* Nails */}
          <Card elevation={2}>
            <CardContent>
              <Typography
                variant="h6"
                gutterBottom
                sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}
              >
                <ThumbUp color="info" />
                Nails
              </Typography>
              <Stack spacing={2}>
                <FormControlLabel
                  control={
                    <Switch checked={nailsEnabled} color="info" size="medium" />
                  }
                  label="Enable Nails"
                  onChange={(event, checked) => setNailsEnabled(checked)}
                />
                {nailsEnabled && (
                  <>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <Chip
                        label={trackNailsSeparately ? "Separate" : "Combined"}
                        color="info"
                        variant="outlined"
                      />
                      <FormControlLabel
                        control={
                          <Switch
                            checked={trackNailsSeparately}
                            color="info"
                            size="medium"
                          />
                        }
                        label="Track Nails Separately"
                        onChange={(event, checked) =>
                          setTrackNailsSeparately(checked)
                        }
                      />
                    </Box>
                    <Divider />
                    <Scoring
                      bonus={nailBonus}
                      onBonusChange={setNailBonus}
                      penalty={nailPenalty}
                      onPenaltyChange={setNailPenalty}
                      minBonus={1}
                      maxBonus={100}
                      minPenalty={0}
                      maxPenalty={100}
                      onErrorChange={setNailScoringError}
                    />
                  </>
                )}
              </Stack>
            </CardContent>
          </Card>

          {/* Tiebreakers */}
          <Card elevation={2}>
            <CardContent>
              <Typography
                variant="h6"
                gutterBottom
                sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}
              >
                <EmojiEvents color="warning" />
                Tiebreakers
              </Typography>
              <Stack spacing={2}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={tiebreakersEnabled}
                      color="warning"
                      size="medium"
                    />
                  }
                  label="Enable Tiebreakers"
                  onChange={(event, checked) => setTiebreakersEnabled(checked)}
                />
                {tiebreakersEnabled && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Chip
                      label={
                        tiebreakerDefaultToGameOfTheWeek
                          ? "Default to GOTW"
                          : "No Default"
                      }
                      color="warning"
                      variant="outlined"
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          checked={tiebreakerDefaultToGameOfTheWeek}
                          color="warning"
                          size="medium"
                        />
                      }
                      label="Default tiebreaker to game of the week"
                      onChange={(event, checked) =>
                        setTiebreakerDefaultToGameOfTheWeek(checked)
                      }
                    />
                  </Box>
                )}
              </Stack>
            </CardContent>
          </Card>

          {/* Season Winner */}
          <Card elevation={2}>
            <CardContent>
              <Typography
                variant="h6"
                gutterBottom
                sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}
              >
                <EmojiEvents color="success" />
                Season Winner
              </Typography>
              <Stack spacing={2}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <Chip
                    label={
                      seasonWinnerOrderBy === "weeklyWins"
                        ? "Weekly Wins"
                        : "Total Points"
                    }
                    color="success"
                    variant="outlined"
                  />
                  <FormControl>
                    <FormLabel>Season winner determined by:</FormLabel>
                    <RadioGroup
                      value={seasonWinnerOrderBy}
                      onChange={(event) =>
                        setSeasonWinnerOrderBy(
                          event.target.value as "weeklyWins" | "totalPoints"
                        )
                      }
                    >
                      <FormControlLabel
                        value="totalPoints"
                        control={<Radio color="success" />}
                        label="Total Points"
                      />
                      <FormControlLabel
                        value="weeklyWins"
                        control={<Radio color="success" />}
                        label="Weekly Wins"
                      />
                    </RadioGroup>
                  </FormControl>
                </Box>
              </Stack>
            </CardContent>
          </Card>

          {/* Pushes */}
          <Card elevation={2}>
            <CardContent>
              <Typography
                variant="h6"
                gutterBottom
                sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}
              >
                <Settings color="primary" />
                Pushes
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Chip
                  label={pushEnabled ? "Enabled" : "Disabled"}
                  color={pushEnabled ? "success" : "default"}
                  variant="outlined"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={pushEnabled}
                      color="primary"
                      size="medium"
                    />
                  }
                  label="Allow pushes when lines are even"
                  onChange={(event, checked) => setPushEnabled(checked)}
                />
              </Box>
            </CardContent>
          </Card>

          {/* Lines (Official vs Custom) */}
          <Card elevation={2}>
            <CardContent>
              <Typography
                variant="h6"
                gutterBottom
                sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}
              >
                <Timeline color="success" />
                Game Lines
              </Typography>
              <Stack spacing={2}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={customGameLinesEnabled}
                      color="success"
                      size="medium"
                    />
                  }
                  label="Custom Lines"
                  onChange={(event, checked) =>
                    setCustomGameLinesEnabled(checked)
                  }
                />
                {customGameLinesEnabled && (
                  <FormControlLabel
                    control={
                      <Switch
                        checked={hideOfficialWhenCustom}
                        color="success"
                        size="medium"
                      />
                    }
                    label="Hide Official Lines"
                    onChange={(event, checked) =>
                      setHideOfficialWhenCustom(checked)
                    }
                  />
                )}
              </Stack>
            </CardContent>
          </Card>

          {/* Flavor Text */}
          <Card elevation={2}>
            <CardContent>
              <Typography
                variant="h6"
                gutterBottom
                sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}
              >
                <TextFields color="success" />
                Flavor Text
              </Typography>
              <Stack spacing={2}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={flavorTextEnabled}
                      color="success"
                      size="medium"
                    />
                  }
                  label="Text for Games"
                  onChange={(event, checked) => setFlavorTextEnabled(checked)}
                />
              </Stack>
            </CardContent>
          </Card>
        </Stack>

        <DialogActions sx={{ mt: 3 }}>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            type="submit"
            variant="contained"
            disabled={!validRules() || isSubmitting}
          >
            {isSubmitting ? "Creating..." : "Create League"}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
};

export default CreateLeagueDialog;
